import {useEffect, useRef, useState} from 'react';
import { ReadCampaign } from '@tinct/core-api-models';
import {useGetCampaignById, useUnpublishCampaign} from '@tinct/core-api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { toast } from 'sonner';
import {useQueryClient} from "@tanstack/react-query";

type UnpublishCampaignModalProps = {
  row: ReadCampaign;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const UnpublishCampaignModal = ({
  row: campaignFromRow,
  open,
  onOpenChange,
}: UnpublishCampaignModalProps) => {
  const queryClient = useQueryClient();
  const [keepPolling, setKeepPolling] = useState(false);
  const isWaitingForJob = useRef(false);

  const { data: campaign } = useGetCampaignById({
    id: campaignFromRow.id as string,
    fields: {
      id: null,
      name: null,
      lastUnpublishedJob: {
        fields: {
          status: null,
        }
      }
    }
  }, {
    enabled: (open || keepPolling) && !!campaignFromRow.id,
    refetchInterval: (query) => {
      return query.state.data?.lastUnpublishedJob?.status === 'running' ? 3000 : false;
    }
  })

  const jobStatus = campaign?.lastUnpublishedJob?.status;

  useEffect(() => {
    if (!isWaitingForJob.current) return;
    if (jobStatus === 'completed' || jobStatus === 'failed') {
      isWaitingForJob.current = false;
      queueMicrotask(() => setKeepPolling(false));
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaignFromRow.id] });
    }
  }, [jobStatus, queryClient, campaignFromRow.id]);

  const { mutate: unpublishCampaign, isPending } = useUnpublishCampaign({
    onSuccess: () => {
      isWaitingForJob.current = true;
      setKeepPolling(true);
      toast.success('Campaign unpublished');
      onOpenChange(false);
    },
  });

  const handleConfirm = () => {
    if (!campaign?.id) return;
    unpublishCampaign({ id: campaign.id });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] rounded-3xl p-8">
        <DialogHeader>
          <DialogTitle>Unpublish campaign</DialogTitle>
          <DialogDescription>
            <span className="font-semibold">{campaign?.name ?? campaignFromRow.name ?? '—'}</span>
            {' will be unpublished and no longer visible to your audience.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending}
          >
            Unpublish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
